import React, { useEffect, useMemo, useState } from 'react';
import { masterAdminApi } from '../services/api';

const STATUS_OPTIONS = ['active', 'inactive', 'suspended'];

function formatDate(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString();
}

export default function MasterControlCenterPage() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [credentialBox, setCredentialBox] = useState(null);
    const [resetBox, setResetBox] = useState(null);
    const [cliKey, setCliKey] = useState('');
    const [installerSecurity, setInstallerSecurity] = useState(null);
    const [rotatedKeyBox, setRotatedKeyBox] = useState(null);
    const [rotatingKey, setRotatingKey] = useState(false);

    const [createForm, setCreateForm] = useState({
        storeName: '',
        storeEmail: '',
        storePhone: '',
        domain: '',
        serverUrl: '',
        masterControlKey: '',
        plan: 'professional',
        months: 1,
        maxUsers: '',
        maxStores: '',
        maxProducts: '',
        featuresCsv: 'pos,inventory,reports,ecommerce',
        adminFirstName: 'Store',
        adminLastName: 'Admin',
        adminEmail: '',
        adminPhone: '',
        adminPassword: '',
    });

    const [subscriptionForm, setSubscriptionForm] = useState({
        tenantId: '',
        plan: 'professional',
        months: 1,
        maxUsers: '',
        maxStores: '',
        maxProducts: '',
        featuresCsv: '',
        adminEmail: '',
        adminPassword: '',
    });

    const [editForm, setEditForm] = useState({
        tenantId: '',
        name: '',
        email: '',
        phone: '',
        businessType: '',
        domain: '',
        serverUrl: '',
        masterControlKey: '',
        status: 'active',
        maxUsers: '',
        maxStores: '',
    });

    const [resetForm, setResetForm] = useState({
        tenantId: '',
        adminEmail: '',
        newEmail: '',
        newPassword: '',
    });

    const filteredStores = useMemo(() => {
        if (statusFilter === 'all') return stores;
        return stores.filter((store) => String(store.status).toLowerCase() === statusFilter);
    }, [stores, statusFilter]);

    const parseOptionalInt = (value) => {
        if (value === '' || value === null || value === undefined) return undefined;
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    };

    const parseFeatures = (csv) => {
        if (!csv || !String(csv).trim()) return undefined;
        const list = String(csv)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        return list.length > 0 ? list : undefined;
    };

    const loadStores = async (nextFilter = statusFilter) => {
        setLoading(true);
        const response = await masterAdminApi.listStores({ status: nextFilter === 'all' ? undefined : nextFilter, limit: 200 });

        if (!response.success) {
            window.alert(response.error || 'Could not load stores');
            setStores([]);
            setLoading(false);
            return;
        }

        const rows = Array.isArray(response.data) ? response.data : [];
        setStores(rows);

        if (rows.length > 0 && !subscriptionForm.tenantId) {
            setSubscriptionForm((prev) => ({ ...prev, tenantId: String(rows[0].id) }));
        }

        if (rows.length > 0 && !editForm.tenantId) {
            const first = rows[0];
            setEditForm({
                tenantId: String(first.id),
                name: first.name || '',
                email: first.email || '',
                phone: first.phone || '',
                businessType: first.businessType || '',
                domain: first.domain || '',
                serverUrl: first.serverUrl || '',
                masterControlKey: '',
                status: first.status || 'active',
                maxUsers: first.maxUsers ?? '',
                maxStores: first.maxStores ?? '',
            });
            setResetForm((prev) => ({ ...prev, tenantId: String(first.id), adminEmail: first.storeAdmin?.email || '' }));
        }

        setLoading(false);
    };

    useEffect(() => {
        loadStores();
        loadInstallerSecurity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadInstallerSecurity() {
        const response = await masterAdminApi.getInstallerSecurity();
        if (!response.success) {
            return;
        }

        setInstallerSecurity(response.data || null);
    }

    const createStoreAdmin = async (event) => {
        event.preventDefault();

        if (!createForm.storeName.trim() || !createForm.adminEmail.trim() || !createForm.adminPassword.trim()) {
            window.alert('Store name, admin email, and admin password are required');
            return;
        }

        const payload = {
            storeName: createForm.storeName.trim(),
            storeEmail: createForm.storeEmail.trim() || undefined,
            storePhone: createForm.storePhone.trim() || undefined,
            domain: createForm.domain.trim() || undefined,
            serverUrl: createForm.serverUrl.trim() || undefined,
            masterControlKey: createForm.masterControlKey.trim() || undefined,
            plan: createForm.plan,
            months: parseOptionalInt(createForm.months) || 1,
            maxUsers: parseOptionalInt(createForm.maxUsers),
            maxStores: parseOptionalInt(createForm.maxStores),
            maxProducts: parseOptionalInt(createForm.maxProducts),
            features: parseFeatures(createForm.featuresCsv),
            adminFirstName: createForm.adminFirstName.trim() || 'Store',
            adminLastName: createForm.adminLastName.trim() || 'Admin',
            adminEmail: createForm.adminEmail.trim(),
            adminPhone: createForm.adminPhone.trim() || undefined,
            adminPassword: createForm.adminPassword,
        };

        const response = await masterAdminApi.createStore(payload);
        if (!response.success) {
            window.alert(response.error || 'Could not create store admin');
            return;
        }

        setCredentialBox(response.data?.login || null);
        setCreateForm((prev) => ({
            ...prev,
            storeName: '',
            storeEmail: '',
            storePhone: '',
            domain: '',
            serverUrl: '',
            masterControlKey: '',
            adminEmail: '',
            adminPhone: '',
            adminPassword: '',
        }));

        window.alert(response.message || 'Store created');
        loadStores();
    };

    const updateStoreStatus = async (tenantId, status) => {
        const response = await masterAdminApi.updateStoreStatus(tenantId, status);
        if (!response.success) {
            window.alert(response.error || 'Could not update status');
            return;
        }

        window.alert(response.message || 'Store status updated');
        loadStores();
    };

    const updateSubscription = async (event) => {
        event.preventDefault();

        if (!subscriptionForm.tenantId) {
            window.alert('Select a store first');
            return;
        }

        const response = await masterAdminApi.updateStoreSubscription(subscriptionForm.tenantId, {
            plan: subscriptionForm.plan,
            months: parseOptionalInt(subscriptionForm.months),
            maxUsers: parseOptionalInt(subscriptionForm.maxUsers),
            maxStores: parseOptionalInt(subscriptionForm.maxStores),
            maxProducts: parseOptionalInt(subscriptionForm.maxProducts),
            features: parseFeatures(subscriptionForm.featuresCsv),
            adminEmail: subscriptionForm.adminEmail?.trim() || undefined,
            adminPassword: subscriptionForm.adminPassword || undefined,
        });

        if (!response.success) {
            window.alert(response.error || 'Could not update subscription');
            return;
        }

        const paymentReference = response.data?.payment?.referenceNumber;
        const adminControl = response.data?.adminControl;
        window.alert(paymentReference
            ? `${response.message || 'Subscription updated'}\nPayment Ref: ${paymentReference}`
            : (response.message || 'Subscription updated'));
        if (adminControl) {
            setResetBox({
                storeName: stores.find((s) => String(s.id) === String(subscriptionForm.tenantId))?.name || 'Selected Store',
                loginUrl: adminControl.loginUrl,
                adminEmail: adminControl.adminEmail,
                newPassword: adminControl.newPassword,
                mustChangePassword: adminControl.mustChangePassword,
            });
            setSubscriptionForm((prev) => ({ ...prev, adminPassword: '' }));
        }
        loadStores();
    };

    const applyStoreSelectionForEditing = (tenantId) => {
        const store = stores.find((entry) => String(entry.id) === String(tenantId));
        if (!store) return;

        setEditForm({
            tenantId: String(store.id),
            name: store.name || '',
            email: store.email || '',
            phone: store.phone || '',
            businessType: store.businessType || '',
            domain: store.domain || '',
            serverUrl: store.serverUrl || '',
            masterControlKey: '',
            status: store.status || 'active',
            maxUsers: store.maxUsers ?? '',
            maxStores: store.maxStores ?? '',
        });
        setSubscriptionForm((prev) => ({ ...prev, tenantId: String(store.id), adminEmail: store.storeAdmin?.email || '' }));
        setResetForm((prev) => ({ ...prev, tenantId: String(store.id), adminEmail: store.storeAdmin?.email || '' }));
    };

    const updateStoreProfile = async (event) => {
        event.preventDefault();
        if (!editForm.tenantId) {
            window.alert('Select a store to edit');
            return;
        }

        const response = await masterAdminApi.updateStoreProfile(editForm.tenantId, {
            name: editForm.name,
            email: editForm.email,
            phone: editForm.phone,
            businessType: editForm.businessType,
            domain: editForm.domain,
            serverUrl: editForm.serverUrl,
            masterControlKey: editForm.masterControlKey || undefined,
            status: editForm.status,
            maxUsers: parseOptionalInt(editForm.maxUsers),
            maxStores: parseOptionalInt(editForm.maxStores),
        });

        if (!response.success) {
            window.alert(response.error || 'Could not update store profile');
            return;
        }

        window.alert(response.message || 'Store profile updated');
        loadStores();
    };

    const resetStorePassword = async (event) => {
        event.preventDefault();
        if (!resetForm.tenantId) {
            window.alert('Select a store first');
            return;
        }

        const response = await masterAdminApi.resetStoreAdminPassword(resetForm.tenantId, {
            adminEmail: resetForm.adminEmail || undefined,
            newEmail: resetForm.newEmail || undefined,
            newPassword: resetForm.newPassword || undefined,
        });

        if (!response.success) {
            window.alert(response.error || 'Could not reset store admin password');
            return;
        }

        setResetBox(response.data
            ? { ...response.data, storeName: stores.find((s) => String(s.id) === String(resetForm.tenantId))?.name || '' }
            : null);
        setResetForm((prev) => ({ ...prev, newPassword: '', newEmail: '' }));
        window.alert(response.message || 'Store admin password reset');
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            window.alert('Copied to clipboard');
        } catch {
            window.alert('Copy failed, please copy manually.');
        }
    };

    const rotateInstallerKey = async () => {
        if (!window.confirm('Rotate installer distribution key now? Existing CLI links using old key will stop working immediately.')) {
            return;
        }

        setRotatingKey(true);
        const response = await masterAdminApi.rotateInstallerDistributionKey({ length: 48 });
        setRotatingKey(false);

        if (!response.success) {
            window.alert(response.error || 'Could not rotate distribution key');
            return;
        }

        const nextKey = response.data?.distributionKey || '';
        setRotatedKeyBox(response.data || null);
        setCliKey(nextKey);
        setInstallerSecurity((prev) => ({
            ...(prev || {}),
            hasDistributionKey: Boolean(nextKey),
            distributionKeyMasked: response.data?.distributionKeyMasked || prev?.distributionKeyMasked,
        }));

        window.alert(response.message || 'Distribution key rotated');
    };

    const baseOrigin = 'https://pos.royaltech.com.ng';
    const encodedKey = encodeURIComponent(cliKey || 'DISTRIBUTION_KEY_HERE');
    const linuxCommand = `mkdir -p ~/royaltechpos && cd ~/royaltechpos && curl --fail --location --show-error "${baseOrigin}/api/public/installer/download/install-sh?k=${encodedKey}" -o install.sh && curl --fail --location --show-error "${baseOrigin}/api/public/installer/download/package/latest?k=${encodedKey}" -o royaltechpos-store.tar.gz && tar -xzf royaltechpos-store.tar.gz --strip-components=1 && chmod +x install.sh && sudo bash install.sh --store --dir /opt/royaltechpos --port 3000 && APP_PORT=${'$'}(awk -F= '/^PORT=/{print ${'$'}2}' /opt/royaltechpos/.env | tail -n1); APP_PORT=${'$'}{APP_PORT:-3000}; curl -fsS "http://127.0.0.1:${'$'}APP_PORT/health"`;
    const windowsCommand = `powershell -NoProfile -ExecutionPolicy Bypass -Command "mkdir C:\\RoyaltechPOS -Force; cd C:\\RoyaltechPOS; Invoke-WebRequest '${baseOrigin}/api/public/installer/download/install-ps1?k=${encodedKey}' -OutFile install.ps1; Invoke-WebRequest '${baseOrigin}/api/public/installer/download/package/latest?k=${encodedKey}' -OutFile royaltechpos-store.tar.gz; tar -xzf royaltechpos-store.tar.gz; .\\install.ps1 -InstallDir 'C:\\RoyaltechPOS' -Port 3000"`;
    const otherServerCommand = `bash -lc 'set -e; WORKDIR=${'$'}{WORKDIR:-$HOME/royaltechpos}; mkdir -p "${'$'}WORKDIR"; cd "${'$'}WORKDIR"; curl --fail --location --show-error "${baseOrigin}/api/public/installer/download/install-sh?k=${encodedKey}" -o install.sh; curl --fail --location --show-error "${baseOrigin}/api/public/installer/download/package/latest?k=${encodedKey}" -o royaltechpos-store.tar.gz; tar -xzf royaltechpos-store.tar.gz --strip-components=1; chmod +x install.sh; sudo ./install.sh --store --dir /opt/royaltechpos --port 3000; APP_PORT=${'$'}(awk -F= "/^PORT=/{print ${'$'}2}" /opt/royaltechpos/.env | tail -n1); APP_PORT=${'$'}{APP_PORT:-3000}; curl -fsS "http://127.0.0.1:${'$'}APP_PORT/health"'`;
    const linuxUpdateCommand = `bash -lc 'set -e; APP_DIR=/opt/royaltechpos; SERVICE=royaltechpos; APP_PORT=${'$'}(awk -F= "/^PORT=/{print ${'$'}2}" "${'$'}APP_DIR/.env" | tail -n1); APP_PORT=${'$'}{APP_PORT:-3000}; TMP=${'$'}(mktemp -d); cd "${'$'}TMP"; curl --fail --location --show-error "${baseOrigin}/api/public/installer/download/package/latest?k=${encodedKey}" -o royaltechpos-store.tar.gz; tar -xzf royaltechpos-store.tar.gz --strip-components=1; sudo rsync -a --delete backend/src/ "${'$'}APP_DIR/src/"; sudo rsync -a --delete backend/migrations/ "${'$'}APP_DIR/migrations/"; sudo cp backend/package.json "${'$'}APP_DIR/package.json"; sudo cp backend/knexfile.js "${'$'}APP_DIR/knexfile.js"; sudo rsync -a --delete frontend/build/ "${'$'}APP_DIR/public/"; cd "${'$'}APP_DIR"; sudo npm install --omit=dev --loglevel=error; sudo NODE_ENV=production ./node_modules/.bin/knex migrate:latest --env production; sudo systemctl restart "${'$'}SERVICE"; curl -fsS "http://127.0.0.1:${'$'}APP_PORT/health"; rm -rf "${'$'}TMP"'`;
    const linuxMinimalUpdateCommand = `bash -lc 'set -e; APP_DIR=/opt/royaltechpos; SERVICE=royaltechpos; APP_PORT=${'$'}(awk -F= "/^PORT=/{print ${'$'}2}" "${'$'}APP_DIR/.env" | tail -n1); APP_PORT=${'$'}{APP_PORT:-3000}; TMP=${'$'}(mktemp -d); cd "${'$'}TMP"; curl --fail --location --show-error "${baseOrigin}/api/public/installer/download/package/latest?k=${encodedKey}" -o royaltechpos-store.tar.gz; tar -xzf royaltechpos-store.tar.gz --strip-components=1; sudo cp backend/src/server.js "${'$'}APP_DIR/src/server.js"; sudo chown root:root "${'$'}APP_DIR/src/server.js"; sudo systemctl restart "${'$'}SERVICE"; curl -fsS "http://127.0.0.1:${'$'}APP_PORT/health"; rm -rf "${'$'}TMP"'`;

    return (
        <div className="products-page">
            <div className="page-header products-header">
                <div>
                    <h2>Master Control Center</h2>
                    <p>Create store admins, control store status, and manage subscriptions</p>
                </div>
                <div className="products-toolbar-actions">
                    <button className="mini-icon-btn" type="button" onClick={() => loadStores()}>↻</button>
                </div>
            </div>

            <section className="products-shell stock-shell">
                <div className="panel stock-panel">
                    <h3>Create Store Admin</h3>
                    <form onSubmit={createStoreAdmin} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))' }}>
                        <label>
                            <span>Store Name</span>
                            <input value={createForm.storeName} onChange={(e) => setCreateForm((prev) => ({ ...prev, storeName: e.target.value }))} />
                        </label>
                        <label>
                            <span>Store Email</span>
                            <input type="email" value={createForm.storeEmail} onChange={(e) => setCreateForm((prev) => ({ ...prev, storeEmail: e.target.value }))} />
                        </label>
                        <label>
                            <span>Store Phone</span>
                            <input value={createForm.storePhone} onChange={(e) => setCreateForm((prev) => ({ ...prev, storePhone: e.target.value }))} />
                        </label>
                        <label>
                            <span>Domain Slug</span>
                            <input value={createForm.domain} onChange={(e) => setCreateForm((prev) => ({ ...prev, domain: e.target.value }))} placeholder="optional" />
                        </label>
                        <label>
                            <span>Store Server URL</span>
                            <input value={createForm.serverUrl} onChange={(e) => setCreateForm((prev) => ({ ...prev, serverUrl: e.target.value }))} placeholder="https://demo.royaltech.com.ng" />
                        </label>
                        <label>
                            <span>Master Control Key</span>
                            <input value={createForm.masterControlKey} onChange={(e) => setCreateForm((prev) => ({ ...prev, masterControlKey: e.target.value }))} placeholder="auto-generated if blank" />
                        </label>
                        <label>
                            <span>Plan</span>
                            <select value={createForm.plan} onChange={(e) => setCreateForm((prev) => ({ ...prev, plan: e.target.value }))}>
                                <option value="starter">starter</option>
                                <option value="professional">professional</option>
                                <option value="enterprise">enterprise</option>
                            </select>
                        </label>
                        <label>
                            <span>Months</span>
                            <input type="number" min="1" value={createForm.months} onChange={(e) => setCreateForm((prev) => ({ ...prev, months: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Users</span>
                            <input type="number" min="1" value={createForm.maxUsers} onChange={(e) => setCreateForm((prev) => ({ ...prev, maxUsers: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Stores</span>
                            <input type="number" min="1" value={createForm.maxStores} onChange={(e) => setCreateForm((prev) => ({ ...prev, maxStores: e.target.value }))} />
                        </label>
                        <label>
                            <span>Admin First Name</span>
                            <input value={createForm.adminFirstName} onChange={(e) => setCreateForm((prev) => ({ ...prev, adminFirstName: e.target.value }))} />
                        </label>
                        <label>
                            <span>Admin Last Name</span>
                            <input value={createForm.adminLastName} onChange={(e) => setCreateForm((prev) => ({ ...prev, adminLastName: e.target.value }))} />
                        </label>
                        <label>
                            <span>Admin Email</span>
                            <input type="email" value={createForm.adminEmail} onChange={(e) => setCreateForm((prev) => ({ ...prev, adminEmail: e.target.value }))} />
                        </label>
                        <label>
                            <span>Admin Password</span>
                            <input type="text" value={createForm.adminPassword} onChange={(e) => setCreateForm((prev) => ({ ...prev, adminPassword: e.target.value }))} />
                        </label>
                        <label style={{ gridColumn: '1 / span 2' }}>
                            <span>Features (comma separated)</span>
                            <input value={createForm.featuresCsv} onChange={(e) => setCreateForm((prev) => ({ ...prev, featuresCsv: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Products</span>
                            <input type="number" min="1" value={createForm.maxProducts} onChange={(e) => setCreateForm((prev) => ({ ...prev, maxProducts: e.target.value }))} />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn" type="submit">Create Store + Admin Login</button>
                        </div>
                    </form>

                    {credentialBox ? (
                        <div style={{ marginTop: 12 }}>
                            <p className="muted-sm" style={{ marginTop: 0 }}>Generated Store Admin Login</p>
                            <div className="products-table-wrap">
                                <table className="products-table admin-table">
                                    <tbody>
                                        <tr><th>Login URL</th><td>{credentialBox.loginUrl}</td></tr>
                                        <tr><th>Email</th><td>{credentialBox.email}</td></tr>
                                        <tr><th>Password</th><td>{credentialBox.password}</td></tr>
                                        {credentialBox.masterControlKey ? <tr><th>Master Control Key</th><td>{credentialBox.masterControlKey}</td></tr> : null}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="panel stock-panel">
                    <h3>Store Installer CLI Commands</h3>
                    <p className="muted-sm" style={{ marginTop: 0 }}>Generate copy-ready command lines for Linux, Windows, and other servers. Master path: https://pos.royaltech.com.ng/admin/master</p>
                    <div className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(180px, 1fr))' }}>
                        <label>
                            <span>Distribution Key</span>
                            <input value={cliKey} onChange={(e) => setCliKey(e.target.value)} placeholder="Enter INSTALLER_DISTRIBUTION_KEY" />
                        </label>
                        <label>
                            <span>Master Admin Portal</span>
                            <input readOnly value="https://pos.royaltech.com.ng/admin/master" />
                        </label>
                    </div>
                    <div className="products-toolbar-actions" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => copyText(linuxCommand)}>Copy Linux Command</button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => copyText(linuxUpdateCommand)}>Copy Linux Update Command</button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => copyText(linuxMinimalUpdateCommand)}>Copy Minimal Update Command</button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => copyText(windowsCommand)}>Copy Windows Command</button>
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => copyText(otherServerCommand)}>Copy Other Server Command</button>
                    </div>
                    <div className="products-table-wrap" style={{ marginTop: 10 }}>
                        <table className="products-table admin-table">
                            <tbody>
                                <tr>
                                    <th style={{ width: 180 }}>Linux</th>
                                    <td><textarea readOnly rows={4} style={{ width: '100%' }} value={linuxCommand} /></td>
                                </tr>
                                <tr>
                                    <th>Linux Update</th>
                                    <td><textarea readOnly rows={6} style={{ width: '100%' }} value={linuxUpdateCommand} /></td>
                                </tr>
                                <tr>
                                    <th>Minimal Update</th>
                                    <td><textarea readOnly rows={4} style={{ width: '100%' }} value={linuxMinimalUpdateCommand} /></td>
                                </tr>
                                <tr>
                                    <th>Windows</th>
                                    <td><textarea readOnly rows={4} style={{ width: '100%' }} value={windowsCommand} /></td>
                                </tr>
                                <tr>
                                    <th>Other Server</th>
                                    <td><textarea readOnly rows={4} style={{ width: '100%' }} value={otherServerCommand} /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="panel stock-panel">
                    <h3>Installer Security Controls</h3>
                    <p className="muted-sm" style={{ marginTop: 0 }}>Rotate distribution key instantly, verify allowlisted deployment IPs, and validate endpoint throttling settings.</p>
                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <tbody>
                                <tr><th>Key Configured</th><td>{installerSecurity?.hasDistributionKey ? 'Yes' : 'No'}</td></tr>
                                <tr><th>Current Key (masked)</th><td>{installerSecurity?.distributionKeyMasked || '-'}</td></tr>
                                <tr><th>Allowlisted IPs</th><td>{Array.isArray(installerSecurity?.allowedIps) && installerSecurity.allowedIps.length > 0 ? installerSecurity.allowedIps.join(', ') : 'No IP allowlist configured (all IPs permitted)'}</td></tr>
                                <tr><th>Rate Limit</th><td>{installerSecurity ? `${installerSecurity.rateLimitMax} requests per ${installerSecurity.rateLimitWindowMinutes} minute(s)` : '-'}</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="products-toolbar-actions" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
                        <button className="btn btn-outline" type="button" disabled={rotatingKey} onClick={rotateInstallerKey}>
                            {rotatingKey ? 'Rotating...' : 'Rotate Distribution Key'}
                        </button>
                        <button className="btn btn-sm" type="button" onClick={loadInstallerSecurity}>Refresh Security Status</button>
                    </div>

                    {rotatedKeyBox ? (
                        <div style={{ marginTop: 12 }}>
                            <p className="muted-sm" style={{ marginTop: 0 }}>New Distribution Key (save securely)</p>
                            <div className="stock-form-grid" style={{ gridTemplateColumns: 'minmax(320px, 1fr) auto auto' }}>
                                <input readOnly value={rotatedKeyBox.distributionKey || ''} />
                                <button className="btn btn-outline btn-sm" type="button" onClick={() => copyText(rotatedKeyBox.distributionKey || '')}>Copy Key</button>
                                <button className="btn btn-sm" type="button" onClick={() => setCliKey(rotatedKeyBox.distributionKey || '')}>Use In CLI Commands</button>
                            </div>
                            <p className="muted-sm" style={{ marginTop: 8 }}>
                                {rotatedKeyBox.persisted
                                    ? `Persisted to environment file: ${rotatedKeyBox.persistedFile || 'configured env file'}`
                                    : 'Persisted in-memory only. Update INSTALLER_DISTRIBUTION_KEY in server environment for restart-safe persistence.'}
                            </p>
                        </div>
                    ) : null}
                </div>

                <div className="panel stock-panel">
                    <h3>Edit Store Information</h3>
                    <form onSubmit={updateStoreProfile} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))' }}>
                        <label>
                            <span>Store</span>
                            <select value={editForm.tenantId} onChange={(e) => applyStoreSelectionForEditing(e.target.value)}>
                                <option value="">Select store</option>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>{store.name}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Name</span>
                            <input value={editForm.name} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} />
                        </label>
                        <label>
                            <span>Email</span>
                            <input type="email" value={editForm.email} onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))} />
                        </label>
                        <label>
                            <span>Phone</span>
                            <input value={editForm.phone} onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))} />
                        </label>
                        <label>
                            <span>Business Type</span>
                            <input value={editForm.businessType} onChange={(e) => setEditForm((prev) => ({ ...prev, businessType: e.target.value }))} />
                        </label>
                        <label>
                            <span>Domain</span>
                            <input value={editForm.domain} onChange={(e) => setEditForm((prev) => ({ ...prev, domain: e.target.value }))} />
                        </label>
                        <label>
                            <span>Store Server URL</span>
                            <input value={editForm.serverUrl} onChange={(e) => setEditForm((prev) => ({ ...prev, serverUrl: e.target.value }))} placeholder="https://demo.royaltech.com.ng" />
                        </label>
                        <label>
                            <span>Master Control Key</span>
                            <input value={editForm.masterControlKey} onChange={(e) => setEditForm((prev) => ({ ...prev, masterControlKey: e.target.value }))} placeholder="leave blank to keep current key" />
                        </label>
                        <label>
                            <span>Status</span>
                            <select value={editForm.status} onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}>
                                <option value="active">active</option>
                                <option value="suspended">suspended</option>
                                <option value="inactive">inactive</option>
                            </select>
                        </label>
                        <label>
                            <span>Max Users</span>
                            <input type="number" min="1" value={editForm.maxUsers} onChange={(e) => setEditForm((prev) => ({ ...prev, maxUsers: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Stores</span>
                            <input type="number" min="1" value={editForm.maxStores} onChange={(e) => setEditForm((prev) => ({ ...prev, maxStores: e.target.value }))} />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn btn-outline" type="submit">Save Store Changes</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <h3>Store Admin Login Access</h3>
                    <p className="muted-sm" style={{ marginTop: 0 }}>
                        Set or replace the login details for a store admin when you need direct control over a store.
                    </p>
                    <form onSubmit={resetStorePassword} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))' }}>
                        <label>
                            <span>Store</span>
                            <select value={resetForm.tenantId} onChange={(e) => setResetForm((prev) => ({ ...prev, tenantId: e.target.value }))}>
                                <option value="">Select store</option>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>{store.name}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Current Admin Email (to identify)</span>
                            <input type="email" value={resetForm.adminEmail} onChange={(e) => setResetForm((prev) => ({ ...prev, adminEmail: e.target.value }))} placeholder="Leave blank to use first admin" />
                        </label>
                        <label>
                            <span>New Login Email (optional)</span>
                            <input type="email" value={resetForm.newEmail} onChange={(e) => setResetForm((prev) => ({ ...prev, newEmail: e.target.value }))} placeholder="Leave blank to keep current" />
                        </label>
                        <label>
                            <span>New Password (optional)</span>
                            <input value={resetForm.newPassword} onChange={(e) => setResetForm((prev) => ({ ...prev, newPassword: e.target.value }))} placeholder="Leave blank to auto-generate" />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn btn-outline" type="submit">Update Login</button>
                        </div>
                    </form>

                    {resetBox ? (
                        <div style={{ marginTop: 12 }}>
                            <p className="muted-sm" style={{ marginTop: 0 }}>Updated Store Admin Credentials</p>
                            <div className="products-table-wrap">
                                <table className="products-table admin-table">
                                    <tbody>
                                        <tr><th>Store</th><td>{resetBox.storeName}</td></tr>
                                        <tr><th>Login URL</th><td>{resetBox.loginUrl}</td></tr>
                                        <tr><th>Admin Email</th><td>{resetBox.adminEmail}</td></tr>
                                        <tr><th>New Password</th><td>{resetBox.newPassword}</td></tr>
                                        <tr>
                                            <th>Remote Store Server</th>
                                            <td>
                                                {resetBox.remoteReset?.attempted
                                                    ? `${resetBox.remoteReset.success ? 'Updated' : 'Failed'} - ${resetBox.remoteReset.message || ''}`
                                                    : 'Not attempted'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="panel stock-panel">
                    <h3>Subscription Controls</h3>
                    <p className="muted-sm" style={{ marginTop: 0 }}>
                        Manage store subscription and optionally set store admin login in the same control flow.
                    </p>
                    <form onSubmit={updateSubscription} className="stock-form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))' }}>
                        <label>
                            <span>Store</span>
                            <select value={subscriptionForm.tenantId} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, tenantId: e.target.value }))}>
                                <option value="">Select store</option>
                                {stores.map((store) => (
                                    <option key={store.id} value={store.id}>{store.name}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Plan</span>
                            <select value={subscriptionForm.plan} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, plan: e.target.value }))}>
                                <option value="starter">starter</option>
                                <option value="professional">professional</option>
                                <option value="enterprise">enterprise</option>
                            </select>
                        </label>
                        <label>
                            <span>Add Months</span>
                            <input type="number" min="1" value={subscriptionForm.months} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, months: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Users</span>
                            <input type="number" min="1" value={subscriptionForm.maxUsers} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, maxUsers: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Stores</span>
                            <input type="number" min="1" value={subscriptionForm.maxStores} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, maxStores: e.target.value }))} />
                        </label>
                        <label>
                            <span>Max Products</span>
                            <input type="number" min="1" value={subscriptionForm.maxProducts} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, maxProducts: e.target.value }))} />
                        </label>
                        <label style={{ gridColumn: '1 / span 2' }}>
                            <span>Features (comma separated)</span>
                            <input value={subscriptionForm.featuresCsv} onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, featuresCsv: e.target.value }))} placeholder="optional" />
                        </label>
                        <label>
                            <span>Store Admin Email (optional)</span>
                            <input
                                type="email"
                                value={subscriptionForm.adminEmail}
                                onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, adminEmail: e.target.value }))}
                                placeholder="admin@store.com"
                            />
                        </label>
                        <label>
                            <span>Set Admin Password (optional)</span>
                            <input
                                type="password"
                                value={subscriptionForm.adminPassword}
                                onChange={(e) => setSubscriptionForm((prev) => ({ ...prev, adminPassword: e.target.value }))}
                                placeholder="set to force update"
                            />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'end' }}>
                            <button className="btn btn-outline" type="submit">Update Subscription</button>
                        </div>
                    </form>
                </div>

                <div className="panel stock-panel">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3>Managed Stores</h3>
                        <label style={{ maxWidth: 220 }}>
                            <span>Status Filter</span>
                            <select
                                value={statusFilter}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setStatusFilter(next);
                                    loadStores(next);
                                }}
                            >
                                <option value="all">All</option>
                                {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                            </select>
                        </label>
                    </div>

                    <div className="products-table-wrap">
                        <table className="products-table admin-table">
                            <thead>
                                <tr>
                                    <th>Store</th>
                                    <th>Store Admin</th>
                                    <th>Status</th>
                                    <th>Plan</th>
                                    <th>Expires</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="dash-empty-row">Loading stores...</td></tr>
                                ) : filteredStores.length === 0 ? (
                                    <tr><td colSpan={6} className="dash-empty-row">No stores found</td></tr>
                                ) : filteredStores.map((store) => (
                                    <tr key={store.id}>
                                        <td>
                                            <strong>{store.name}</strong>
                                            <div className="muted-sm">{store.domain || '-'}</div>
                                        </td>
                                        <td>
                                            {store.storeAdmin?.email || '-'}
                                            <div className="muted-sm">{store.storeAdmin?.firstName || ''} {store.storeAdmin?.lastName || ''}</div>
                                            {store.storeAdmin?.mustChangePassword ? (
                                                <div className="muted-sm" style={{ color: '#b45309' }}>Password update pending</div>
                                            ) : null}
                                        </td>
                                        <td>{store.status}</td>
                                        <td>{store.subscriptionPlan}</td>
                                        <td>{formatDate(store.subscriptionEnd)}</td>
                                        <td>
                                            <div className="products-toolbar-actions" style={{ gap: 8 }}>
                                                <button className="btn btn-sm" type="button" onClick={() => {
                                                    setSubscriptionForm((prev) => ({ ...prev, tenantId: String(store.id) }));
                                                    applyStoreSelectionForEditing(store.id);
                                                    setResetForm((prev) => ({ ...prev, tenantId: String(store.id), adminEmail: store.storeAdmin?.email || '' }));
                                                }}>Manage</button>
                                                <button className="btn btn-outline btn-sm" type="button" onClick={() => updateStoreStatus(store.id, 'active')}>Activate</button>
                                                <button className="btn btn-outline btn-sm" type="button" onClick={() => updateStoreStatus(store.id, 'suspended')}>Suspend</button>
                                                <button className="btn btn-outline btn-sm" type="button" onClick={() => updateStoreStatus(store.id, 'inactive')}>Deactivate</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}
